import { fetchWithSupabaseAuth } from "./supabaseClient.js";

export const PORTAL_PUBLICATION_STATUSES = {
  published: "published",
  unavailable: "unavailable",
  unpublished: "unpublished",
};

const PORTAL_PUBLICATION_UNAVAILABLE_REASON_MESSAGES = {
  missing_assignment: "Aucune tournée calculée n’est disponible pour ce camion.",
  missing_driver_account: "Aucun compte chauffeur n’est lié à cette tournée.",
  missing_tractor: "Cette remorque n’a pas de camion dans ce dispatch. Choisissez son camion avant de publier.",
  unresolved_driver_assignment: "Sélectionnez un employé chauffeur pour confirmer l’affectation de ce dispatch.",
  missing_snapshot: "Lancez l’optimisation avant de publier cette journée.",
  optimization_in_progress: "Une optimisation est en cours. Attendez sa fin avant de publier.",
  pending_source_changes: "Cette tournée contient une ou plusieurs commandes touchées par les mises à jour en attente.",
  pending_truck_source_changes: "Le camion de cette tournée est touché par une mise à jour en attente.",
  end_of_day_load_noncompliant: "Ce camion contient un Chargé PM à corriger.",
  end_of_day_load_target_snapshot_missing: "Le dispatch cible d’un Chargé PM est absent.",
  end_of_day_load_validation_unavailable: "La vérification des Chargés PM n’a pas pu être complétée.",
  stale_snapshot: "Dispatch à revalider : traitez « Mise à jour » ou relancez « Optimiser » avant de publier.",
  truck_dispatch_disabled: "Ce camion est hors dispatch. Réactivez-le avant de publier sa tournée.",
  truck_inactive: "Ce camion est inactif pour cette journée.",
  truck_not_found: "Ce camion est introuvable dans la flotte active.",
};

const PORTAL_PUBLICATION_POLICY_ACTIONS = {
  pending_source_changes: "Ouvrir M-à-J commandes",
  pending_truck_source_changes: "Ouvrir M-à-J camions",
  end_of_day_load_noncompliant: "Ouvrir M-à-J commandes",
  end_of_day_load_target_snapshot_missing: "Optimiser le dispatch cible",
  end_of_day_load_validation_unavailable: "Réessayer la vérification M-à-J",
};

export const TRUCK_DISPATCH_DISABLED_PORTAL_PUBLISH_REASON = "Ce camion est hors dispatch. Réactivez-le dans le dispatch avant de publier sa tournée sur le portail chauffeur.";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function resolvePortalPublicationUnavailableReason(value) {
  const normalizedValue = normalizeInlineText(value);
  return PORTAL_PUBLICATION_UNAVAILABLE_REASON_MESSAGES[normalizedValue] || normalizedValue;
}

export function resolvePortalPublishDisabledReason({
  portalPublicationDisabledReason = "",
  unpublishedPortalRoutes = [],
  isPortalPublicationAllowed = true,
  portalPublicationUnavailableReason = "",
  unavailablePortalRoutes = [],
  publishedPortalRoutes = [],
} = {}) {
  const normalizedDisabledReason = normalizeInlineText(portalPublicationDisabledReason);
  if (normalizedDisabledReason) {
    return normalizedDisabledReason;
  }

  if (unpublishedPortalRoutes.length === 0) {
    if (!isPortalPublicationAllowed) {
      return normalizeInlineText(portalPublicationUnavailableReason);
    }

    if (unavailablePortalRoutes.length > 0 && publishedPortalRoutes.length === 0) {
      return "Aucun chauffeur lié à une tournée publiable.";
    }

    return "Toutes les tournées disponibles sont déjà publiées.";
  }

  return "";
}

export function resolveTruckPortalPublishDisabledReason({
  canPublishTruckPortal = false,
  isDispatchDisabled = false,
  isPortalPublished = false,
  isPortalPublicationLoading = false,
  portalPublicationErrorMessage = "",
  portalPublicationRoute = null,
  publicationUnavailableReason = "",
  isPortalPublishGloballyUnavailable = false,
  portalPublicationStatus = PORTAL_PUBLICATION_STATUSES.unavailable,
  truckId = "",
} = {}) {
  if (canPublishTruckPortal || isPortalPublished) {
    return "";
  }

  if (isDispatchDisabled) {
    return TRUCK_DISPATCH_DISABLED_PORTAL_PUBLISH_REASON;
  }

  if (isPortalPublicationLoading) {
    return "Chargement de l'état de publication…";
  }

  const normalizedErrorMessage = normalizeInlineText(portalPublicationErrorMessage);
  if (normalizedErrorMessage) {
    return normalizedErrorMessage;
  }

  const hasRoutePublicationPolicy = typeof portalPublicationRoute?.publicationAllowed === "boolean";
  const routePublicationUnavailableReason = normalizeInlineText(
    portalPublicationRoute?.publicationUnavailableReason,
  );
  const routeUnavailableReason = normalizeInlineText(portalPublicationRoute?.unavailableReason);
  const outputPolicyReason = normalizeInlineText(portalPublicationRoute?.outputPolicy?.reason);
  if (hasRoutePublicationPolicy && portalPublicationRoute.publicationAllowed === false) {
    return outputPolicyReason
      || routePublicationUnavailableReason
      || routeUnavailableReason
      || normalizeInlineText(publicationUnavailableReason);
  }

  const normalizedGlobalReason = normalizeInlineText(publicationUnavailableReason);
  if (!hasRoutePublicationPolicy && isPortalPublishGloballyUnavailable && normalizedGlobalReason) {
    return normalizedGlobalReason;
  }
  const fallbackGlobalReason = hasRoutePublicationPolicy ? "" : normalizedGlobalReason;

  if (routeUnavailableReason) {
    return routeUnavailableReason;
  }

  if (!truckId) {
    return "Aucun camion sélectionné.";
  }

  if (!portalPublicationRoute) {
    return fallbackGlobalReason || resolvePortalPublicationUnavailableReason("missing_assignment");
  }

  if (
    portalPublicationStatus === PORTAL_PUBLICATION_STATUSES.unpublished
    && !normalizeInlineText(portalPublicationRoute.planHash)
  ) {
    return fallbackGlobalReason || resolvePortalPublicationUnavailableReason("missing_assignment");
  }

  if (portalPublicationStatus === PORTAL_PUBLICATION_STATUSES.unavailable) {
    return fallbackGlobalReason || resolvePortalPublicationUnavailableReason("missing_driver_account");
  }

  return fallbackGlobalReason;
}

export function normalizePortalPublicationDispatchPeriod(value) {
  const normalizedValue = normalizeInlineText(value).toLowerCase();
  return normalizedValue === "evening" || normalizedValue === "soir" ? "evening" : "day";
}

export function normalizePortalPublicationInvalidatedTruckIds(value = []) {
  return Array.from(new Set(
    (Array.isArray(value) ? value : [])
      .map(normalizeInlineText)
      .filter(Boolean),
  ));
}

export function buildPortalPublicationInvalidationToast(truckIds = []) {
  const normalizedTruckIds = normalizePortalPublicationInvalidatedTruckIds(truckIds);
  if (normalizedTruckIds.length === 0) {
    return null;
  }

  const truckLabels = normalizedTruckIds.join(", ");
  const isSingleTruck = normalizedTruckIds.length === 1;
  return {
    duration: 8000,
    message: isSingleTruck
      ? `La publication du camion ${truckLabels} n'est plus active dans le portail chauffeur. Vérifiez sa tournée avant de la republier.`
      : `Les publications des camions ${truckLabels} ne sont plus actives dans le portail chauffeur. Vérifiez ces ${normalizedTruckIds.length} tournées avant de les republier.`,
    title: isSingleTruck ? "Publication portail retirée" : "Publications portail retirées",
    type: "info",
  };
}

function normalizeCount(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? Math.round(numericValue) : 0;
}

function normalizePortalPublicationStatus(value) {
  const normalizedValue = normalizeInlineText(value).toLowerCase();
  if (normalizedValue === PORTAL_PUBLICATION_STATUSES.published) {
    return PORTAL_PUBLICATION_STATUSES.published;
  }
  if (normalizedValue === PORTAL_PUBLICATION_STATUSES.unavailable) {
    return PORTAL_PUBLICATION_STATUSES.unavailable;
  }
  return PORTAL_PUBLICATION_STATUSES.unpublished;
}

function normalizeRevision(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numericValue = Number(value);
  if (Number.isInteger(numericValue) && numericValue > 0) {
    return numericValue;
  }
  return null;
}

function normalizePortalPublicationPolicyTarget(value = null) {
  const safeTarget = value && typeof value === "object" ? value : {};
  const dispatchDate = normalizeInlineText(safeTarget.dispatch_date || safeTarget.dispatchDate);
  const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
    safeTarget.dispatch_period || safeTarget.dispatchPeriod,
  );
  const truckId = normalizeInlineText(safeTarget.truck_id || safeTarget.truckId);
  const truckName = normalizeInlineText(
    safeTarget.truck_name
    || safeTarget.truckName
    || safeTarget.truck_label
    || safeTarget.truckLabel,
  );
  if (!dispatchDate && !truckId && !truckName) {
    return null;
  }
  return {
    dispatchDate,
    dispatchPeriod,
    truckId,
    truckName,
  };
}

function normalizePortalPublicationTruckPolicyEntry(value = null, fallbackTruckId = "") {
  const safePolicy = value && typeof value === "object" ? value : {};
  const truckId = normalizeInlineText(
    safePolicy.truck_id
    || safePolicy.truckId
    || fallbackTruckId,
  );
  if (!truckId) {
    return null;
  }
  const reasonCode = normalizeInlineText(safePolicy.reason_code || safePolicy.reasonCode).toLowerCase();
  // Rappel non bloquant (ex. dispatch cible d'un Chargé PM pas encore optimisé).
  const rawNotice = safePolicy.notice && typeof safePolicy.notice === "object" ? safePolicy.notice : null;
  const noticeReasonCode = normalizeInlineText(rawNotice?.reason_code || rawNotice?.reasonCode).toLowerCase();
  return {
    actionRequired: normalizeInlineText(safePolicy.action_required || safePolicy.actionRequired)
      || PORTAL_PUBLICATION_POLICY_ACTIONS[reasonCode]
      || "",
    notice: rawNotice
      ? {
        actionRequired: normalizeInlineText(rawNotice.action_required || rawNotice.actionRequired)
          || PORTAL_PUBLICATION_POLICY_ACTIONS[noticeReasonCode]
          || "",
        reason: normalizeInlineText(rawNotice.reason) || resolvePortalPublicationUnavailableReason(noticeReasonCode),
        target: normalizePortalPublicationPolicyTarget(rawNotice.target),
      }
      : null,
    blocked: safePolicy.blocked === true,
    markerKeys: normalizePortalPublicationInvalidatedTruckIds(
      safePolicy.marker_keys || safePolicy.markerKeys,
    ),
    orderIds: normalizePortalPublicationInvalidatedTruckIds(
      safePolicy.order_ids || safePolicy.orderIds,
    ),
    reason: normalizeInlineText(safePolicy.reason)
      || resolvePortalPublicationUnavailableReason(reasonCode),
    reasonCode,
    target: normalizePortalPublicationPolicyTarget(safePolicy.target),
    truckId,
  };
}

export function normalizePortalPublicationTruckOutputPolicy(value = null) {
  const safePolicy = value && typeof value === "object" ? value : {};
  const rawTrucks = safePolicy.trucks && typeof safePolicy.trucks === "object"
    ? safePolicy.trucks
    : {};
  const trucks = {};
  Object.entries(rawTrucks).forEach(([truckId, policy]) => {
    const normalizedPolicy = normalizePortalPublicationTruckPolicyEntry(policy, truckId);
    if (normalizedPolicy) {
      trucks[normalizedPolicy.truckId] = normalizedPolicy;
    }
  });
  const blockedTruckIds = normalizePortalPublicationInvalidatedTruckIds(
    safePolicy.blocked_truck_ids || safePolicy.blockedTruckIds,
  );
  Object.values(trucks).forEach((policy) => {
    if (policy.blocked && !blockedTruckIds.includes(policy.truckId)) {
      blockedTruckIds.push(policy.truckId);
    }
  });
  return {
    blockedTruckIds,
    status: normalizeInlineText(safePolicy.status).toLowerCase(),
    trucks,
  };
}

function formatPortalPublicationPolicyTarget(target = null) {
  if (!target || typeof target !== "object") {
    return "";
  }
  const periodLabel = target.dispatchPeriod === "evening" ? "Soir" : "Jour";
  const truckLabel = target.truckName || target.truckId;
  return [
    target.dispatchDate,
    target.dispatchDate ? periodLabel : "",
    truckLabel ? `camion ${truckLabel}` : "",
  ].filter(Boolean).join(" · ");
}

export function buildPortalPublicationActionHoverCardModel({
  reason = "",
  routes = [],
  truckIds = null,
  noticeTruckIds = truckIds,
  truckOutputPolicy = null,
} = {}) {
  const normalizedPolicy = normalizePortalPublicationTruckOutputPolicy(truckOutputPolicy);
  const normalizedRoutes = (Array.isArray(routes) ? routes : [])
    .map(normalizePortalPublicationRoute)
    .filter((route) => route.truckId);
  const routeByTruckId = new Map(normalizedRoutes.map((route) => [route.truckId, route]));
  const requestedTruckIds = Array.isArray(truckIds)
    ? new Set(truckIds.map(normalizeInlineText).filter(Boolean))
    : null;
  const policiesByTruckId = new Map();
  Object.values(normalizedPolicy.trucks).forEach((policy) => {
    policiesByTruckId.set(policy.truckId, policy);
  });
  normalizedRoutes.forEach((route) => {
    if (route.outputPolicy && !policiesByTruckId.has(route.truckId)) {
      policiesByTruckId.set(route.truckId, route.outputPolicy);
    }
  });
  const blockers = [...policiesByTruckId.values()]
    .filter((policy) => policy.blocked)
    .filter((policy) => requestedTruckIds === null || requestedTruckIds.has(policy.truckId))
    .map((policy) => {
      const route = routeByTruckId.get(policy.truckId);
      return {
        actionRequired: policy.actionRequired,
        reason: policy.reason || resolvePortalPublicationUnavailableReason(policy.reasonCode),
        reasonCode: policy.reasonCode,
        targetLabel: formatPortalPublicationPolicyTarget(policy.target),
        truckId: policy.truckId,
        truckLabel: route?.truckName || policy.truckId,
      };
    });
  const notices = [...policiesByTruckId.values()]
    .filter((policy) => policy.notice && !policy.blocked)
    .filter((policy) => !Array.isArray(noticeTruckIds) || noticeTruckIds.includes(policy.truckId))
    .map((policy) => ({
      actionRequired: policy.notice.actionRequired,
      reason: policy.notice.reason,
      targetLabel: formatPortalPublicationPolicyTarget(policy.notice.target),
      truckId: policy.truckId,
      truckLabel: routeByTruckId.get(policy.truckId)?.truckName || policy.truckId,
    }));
  return {
    blockers,
    notices,
    reason: normalizeInlineText(reason)
      || blockers[0]?.reason
      || (notices.length ? "" : "La publication portail n'est pas disponible pour le moment."),
  };
}

export function normalizePortalPublicationRoute(route = null) {
  const safeRoute = route && typeof route === "object" ? route : {};
  const revision = normalizeRevision(safeRoute.revision ?? safeRoute.expectedRevision);
  const rawPublicationAllowed = safeRoute.publication_allowed ?? safeRoute.publicationAllowed;

  return {
    dispatchPeriod: normalizePortalPublicationDispatchPeriod(safeRoute.dispatch_period || safeRoute.dispatchPeriod),
    driverName: normalizeInlineText(safeRoute.driver_name || safeRoute.driverName),
    driverAssignmentVersion: normalizeRevision(safeRoute.driver_assignment_version ?? safeRoute.driverAssignmentVersion),
    expectedPlanHash: normalizeInlineText(safeRoute.plan_hash || safeRoute.planHash || safeRoute.expectedPlanHash),
    expectedRevision: revision,
    isDispatchDisabled: safeRoute.is_dispatch_disabled === true || safeRoute.isDispatchDisabled === true,
    orderCount: normalizeCount(safeRoute.order_count ?? safeRoute.orderCount),
    outputPolicy: normalizePortalPublicationTruckPolicyEntry(
      safeRoute.output_policy || safeRoute.outputPolicy,
      safeRoute.truck_id || safeRoute.truckId,
    ),
    planHash: normalizeInlineText(safeRoute.plan_hash || safeRoute.planHash || safeRoute.expectedPlanHash),
    publicationAllowed: rawPublicationAllowed === undefined || rawPublicationAllowed === null
      ? null
      : rawPublicationAllowed === true,
    publicationUnavailableReason: resolvePortalPublicationUnavailableReason(
      safeRoute.publication_unavailable_reason || safeRoute.publicationUnavailableReason,
    ),
    publishedAt: normalizeInlineText(safeRoute.published_at || safeRoute.publishedAt),
    publishedByUserId: normalizeInlineText(safeRoute.published_by_user_id || safeRoute.publishedByUserId),
    revision,
    status: normalizePortalPublicationStatus(safeRoute.status),
    targetDriverUserId: normalizeInlineText(safeRoute.target_driver_user_id || safeRoute.targetDriverUserId),
    truckId: normalizeInlineText(safeRoute.truck_id || safeRoute.truckId),
    truckName: normalizeInlineText(safeRoute.truck_name || safeRoute.truckName),
    unavailableReason: resolvePortalPublicationUnavailableReason(safeRoute.unavailable_reason || safeRoute.unavailableReason),
    withdrawalReason: normalizeInlineText(safeRoute.withdrawal_reason || safeRoute.withdrawalReason),
    withdrawnAt: normalizeInlineText(safeRoute.withdrawn_at || safeRoute.withdrawnAt),
  };
}

function buildPortalPublicationDriverEntry(route) {
  const normalizedRoute = normalizePortalPublicationRoute(route);
  const truckLabel = normalizedRoute.truckName || normalizedRoute.truckId;

  return {
    key: normalizedRoute.truckId || `${normalizedRoute.driverName}:${truckLabel}`,
    label: normalizedRoute.driverName || "Chauffeur non renseigné",
    meta: truckLabel ? `Camion ${truckLabel}` : "",
    sortKey: `${normalizedRoute.driverName || "zzz"}|${truckLabel || ""}`.toLowerCase(),
  };
}

function sortPortalPublicationDriverEntries(entries = []) {
  return [...entries].sort((left, right) => left.sortKey.localeCompare(right.sortKey, "fr", { sensitivity: "base" }));
}

export function buildPortalPublicationDriverHoverCardModel({
  routes = [],
  publishedCount = null,
  availableCount = null,
  errorMessage = "",
  isLoading = false,
} = {}) {
  const normalizedRoutes = (Array.isArray(routes) ? routes : [])
    .map(normalizePortalPublicationRoute)
    .filter((route) => route.truckId);
  const publishedDrivers = sortPortalPublicationDriverEntries(
    normalizedRoutes
      .filter((route) => route.status === PORTAL_PUBLICATION_STATUSES.published)
      .map(buildPortalPublicationDriverEntry),
  ).map(({ sortKey, ...entry }) => entry);
  const eligibleUnpublishedDrivers = sortPortalPublicationDriverEntries(
    normalizedRoutes
      .filter((route) => route.status === PORTAL_PUBLICATION_STATUSES.unpublished)
      .map(buildPortalPublicationDriverEntry),
  ).map(({ sortKey, ...entry }) => entry);
  const resolvedPublishedCount = publishedCount === null || publishedCount === undefined
    ? publishedDrivers.length
    : normalizeCount(publishedCount);
  const resolvedAvailableCount = availableCount === null || availableCount === undefined
    ? resolvedPublishedCount + eligibleUnpublishedDrivers.length
    : normalizeCount(availableCount);
  const normalizedErrorMessage = normalizeInlineText(errorMessage);
  const statusMessage = isLoading
    ? "Chargement de l'état de publication…"
    : normalizedErrorMessage;
  const publishedLabel = resolvedPublishedCount === 1 ? "chauffeur publié" : "chauffeurs publiés";
  const eligibleLabel = resolvedAvailableCount === 1 ? "éligible" : "éligibles";
  const summary = statusMessage
    ? ""
    : `${resolvedPublishedCount} ${publishedLabel} sur ${resolvedAvailableCount} ${eligibleLabel}.`;

  return {
    summary,
    statusMessage,
    publishedDrivers,
    eligibleUnpublishedDrivers,
    emptyPublishedLabel: "Aucun chauffeur publié pour le moment.",
    emptyEligibleLabel: "Tous les chauffeurs éligibles sont publiés.",
  };
}

export function buildPortalPublicationSelectionSummary(routes = []) {
  const routesByTruckId = new Map();
  (Array.isArray(routes) ? routes : []).forEach((route) => {
    const normalizedRoute = normalizePortalPublicationRoute(route);
    if (normalizedRoute.truckId) {
      routesByTruckId.set(normalizedRoute.truckId, normalizedRoute);
    }
  });
  const selectedRoutes = Array.from(routesByTruckId.values());
  const selectedRoute = selectedRoutes.length === 1 ? selectedRoutes[0] : null;

  return {
    driverName: selectedRoute?.driverName || "",
    driverCount: new Set(selectedRoutes.map((route) => route.targetDriverUserId).filter(Boolean)).size,
    orderCount: selectedRoutes.reduce((total, route) => total + route.orderCount, 0),
    routeCount: selectedRoutes.length,
    truckName: selectedRoute?.truckName || selectedRoute?.truckId || "",
  };
}

export function normalizePortalPublications(payload = null, defaults = {}) {
  const source = payload?.portal_publications && typeof payload.portal_publications === "object"
    ? payload.portal_publications
    : payload && typeof payload === "object"
      ? payload
      : {};
  const truckOutputPolicy = normalizePortalPublicationTruckOutputPolicy(
    source.truck_output_policy
    || source.truckOutputPolicy
    || defaults.truckOutputPolicy,
  );
  const routes = Array.isArray(source.routes)
    ? source.routes.map(normalizePortalPublicationRoute).filter((route) => route.truckId).map((route) => {
        const outputPolicy = truckOutputPolicy.trucks[route.truckId] || route.outputPolicy;
        return {
          ...route,
          outputPolicy,
          publicationAllowed: outputPolicy?.blocked === true ? false : route.publicationAllowed,
          publicationUnavailableReason: outputPolicy?.blocked === true
            ? outputPolicy.reason || resolvePortalPublicationUnavailableReason(outputPolicy.reasonCode)
            : route.publicationUnavailableReason,
        };
      })
    : [];
  const sourceSummary = source.summary && typeof source.summary === "object" ? source.summary : {};
  const publicationUnavailableReason = resolvePortalPublicationUnavailableReason(
    source.publication_unavailable_reason || source.publicationUnavailableReason,
  );
  const rawPublicationAllowed = source.publication_allowed ?? source.publicationAllowed;
  const derivedPublishedCount = routes.filter((route) => route.status === PORTAL_PUBLICATION_STATUSES.published).length;
  const derivedUnavailableCount = routes.filter((route) => route.status === PORTAL_PUBLICATION_STATUSES.unavailable).length;
  const derivedUnpublishedCount = routes.filter((route) => route.status === PORTAL_PUBLICATION_STATUSES.unpublished).length;
  const totalCount = normalizeCount(sourceSummary.total_count ?? sourceSummary.totalCount) || routes.length;
  const unavailableCount = sourceSummary.unavailable_count === undefined && sourceSummary.unavailableCount === undefined
    ? derivedUnavailableCount
    : normalizeCount(sourceSummary.unavailable_count ?? sourceSummary.unavailableCount);
  const publishedCount = sourceSummary.published_count === undefined && sourceSummary.publishedCount === undefined
    ? derivedPublishedCount
    : normalizeCount(sourceSummary.published_count ?? sourceSummary.publishedCount);
  const unpublishedCount = sourceSummary.unpublished_count === undefined && sourceSummary.unpublishedCount === undefined
    ? derivedUnpublishedCount
    : normalizeCount(sourceSummary.unpublished_count ?? sourceSummary.unpublishedCount);
  const availableCount = sourceSummary.available_count === undefined && sourceSummary.availableCount === undefined
    ? Math.max(totalCount - unavailableCount, 0)
    : normalizeCount(sourceSummary.available_count ?? sourceSummary.availableCount);

  return {
    dispatchDate: normalizeInlineText(source.dispatch_date || source.dispatchDate) || normalizeInlineText(defaults.dispatchDate),
    dispatchPeriod: normalizePortalPublicationDispatchPeriod(source.dispatch_period || source.dispatchPeriod || defaults.dispatchPeriod),
    publicationAllowed: rawPublicationAllowed === undefined
      ? !publicationUnavailableReason
      : rawPublicationAllowed === true,
    publicationUnavailableCode: normalizeInlineText(source.publication_unavailable_reason || source.publicationUnavailableReason),
    publicationUnavailableReason,
    routes,
    truckOutputPolicy,
    summary: {
      availableCount,
      publishedCount,
      totalCount,
      unavailableCount,
      unpublishedCount,
    },
  };
}

export function findPortalPublicationRoute(portalPublications = null, truckId = "") {
  const safeTruckId = normalizeInlineText(truckId);
  if (!safeTruckId) {
    return null;
  }
  const normalizedPortalPublications = normalizePortalPublications(portalPublications);
  return normalizedPortalPublications.routes.find((route) => route.truckId === safeTruckId) || null;
}

export function buildPortalPublicationsUrl(dispatchDate = "", dispatchPeriod = "day") {
  const safeDispatchDate = normalizeInlineText(dispatchDate);
  if (!safeDispatchDate) {
    return "";
  }
  const searchParams = new URLSearchParams({
    dispatch_period: normalizePortalPublicationDispatchPeriod(dispatchPeriod),
  });
  return `/api/dispatch-days/${encodeURIComponent(safeDispatchDate)}/portal-publications?${searchParams.toString()}`;
}

export function buildPortalPublicationMutationUrl(dispatchDate = "", action = "publish") {
  const safeDispatchDate = normalizeInlineText(dispatchDate);
  if (!safeDispatchDate) {
    return "";
  }
  const suffix = normalizeInlineText(action).toLowerCase() === "unpublish"
    ? "unpublish-portal"
    : "publish-portal";
  return `/api/dispatch-days/${encodeURIComponent(safeDispatchDate)}/${suffix}`;
}

export function buildPortalPublicationMutationBody(action = "publish", routes = [], dispatchPeriod = "day") {
  const normalizedAction = normalizeInlineText(action).toLowerCase() === "unpublish" ? "unpublish" : "publish";
  const normalizedRoutes = (Array.isArray(routes) ? routes : [])
    .map(normalizePortalPublicationRoute)
    .filter((route) => route.truckId)
    .map((route) => (normalizedAction === "unpublish"
      ? {
          truck_id: route.truckId,
          expected_revision: route.revision,
        }
      : {
          truck_id: route.truckId,
          expected_plan_hash: route.planHash,
          expected_driver_user_id: route.targetDriverUserId,
          expected_assignment_version: route.driverAssignmentVersion,
        }))
    .filter((route) => normalizedAction === "unpublish"
      ? route.expected_revision !== null
      : Boolean(route.expected_plan_hash));

  return {
    dispatch_period: normalizePortalPublicationDispatchPeriod(dispatchPeriod),
    routes: normalizedRoutes,
  };
}

function resolvePortalPublicationErrorMessage(payload, fallbackMessage) {
  const candidates = [payload?.detail, payload?.error, payload?.message];
  return candidates.find((value) => typeof value === "string" && value.trim())?.trim() || fallbackMessage;
}

export async function fetchPortalPublications(dispatchDate = "", dispatchPeriod = "day") {
  const requestUrl = buildPortalPublicationsUrl(dispatchDate, dispatchPeriod);
  if (!requestUrl) {
    return normalizePortalPublications(null, { dispatchDate, dispatchPeriod });
  }

  const response = await fetchWithSupabaseAuth(requestUrl, { cache: "no-store" });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(resolvePortalPublicationErrorMessage(
      payload,
      "Impossible de charger les publications du portail chauffeur.",
    ));
  }

  return normalizePortalPublications(payload, { dispatchDate, dispatchPeriod });
}

export async function publishPortalRoutes(dispatchDate = "", routes = [], dispatchPeriod = "day") {
  const requestUrl = buildPortalPublicationMutationUrl(dispatchDate, "publish");
  const mutationBody = buildPortalPublicationMutationBody("publish", routes, dispatchPeriod);
  if (!requestUrl || mutationBody.routes.length === 0) {
    throw new Error("Impossible de publier cette tournée dans le portail chauffeur.");
  }

  const response = await fetchWithSupabaseAuth(requestUrl, {
    body: JSON.stringify(mutationBody),
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(resolvePortalPublicationErrorMessage(
      payload,
      "Impossible de publier les tournées dans le portail chauffeur.",
    ));
  }

  return payload;
}
